import * as _ from 'lodash';
import * as moment from 'moment';
import { Component, ElementRef, Input, OnDestroy,EventEmitter,Output, ViewChild, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { interval, Observable, Subject } from 'rxjs';
import { concatMap, filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-counter',
  templateUrl: './counter.component.html',
  styleUrls: ['./counter.component.scss']
})
export class CounterComponent implements OnDestroy {
  @ViewChild('block', { static: true }) blockRef: ElementRef;
  @ViewChild('block2', { static: true }) bloc2kRef: ElementRef;

  @Output() toggleBtn = new EventEmitter<string>();
  @Output() changeStatus = new EventEmitter<string>();

  @Input() mode: number = 1;
  @Input() customStyles: string = '';
  @Input() campaign: any = null;
  @Input() segments: any[] = [{
    label: 'days',
    duration: 60*60*24,
    required: false
  }, {
    label: 'hours',
    duration: 60*60,
    required: false
  }, {
    label: 'minutes',
    duration: 60,
    required: true
  }, {
    label: 'seconds',
    duration: 1,
    required: true
  }];

  public startDateTime: any = null;
  public endDateTime: any = null;
  public closeDateTime: any = null;

  public value$: Observable<number> = interval(1000).pipe(
    map(() => {
      return moment().unix();
    })
  );

  public auctionControlMethod = 0;
  public autoCloseEnabled = false;

  public startingIn$: Observable<any> = null;
  public startingIn: any[] = [];
  public startingInFormat$: Observable<any> = null;
  public startingInFormat: string = '';
  public isStartingIn$: Observable<any> = null;
  public isStartingIn: boolean = false;

  public endsIn$: Observable<any> = null;
  public endsIn: any[] = [];
  public endsInFormat$: Observable<any> = null;
  public endsInFormat: string = '';
  public isEndsIn$: Observable<any> = null;
  public isEndsIn: boolean = false;

  public closingIn$: Observable<any> = null;
  public closingIn: any[] = [];
  public closingInFormat$: Observable<any> = null;
  public closingInFormat: string = '';
  public isClosingIn$: Observable<any> = null;
  public isClosingIn: boolean = false;

  public takeUntilSubject: Subject<any> = new Subject();

  constructor(
    public cdr: ChangeDetectorRef
  ) { }

  private _transformSegments(timestamp) {
    let display = false;
    return _.chain(this.segments).map(segment => {
      const remainder = timestamp % segment.duration;
      const value = (timestamp - remainder) / segment.duration;
      timestamp = remainder;
      display = (value > 0) || segment.required || display;
      return { ...segment, display, value };
    }).filter(segment => {
      return segment.display;
    }).value();
  }

  private _transformFormat(datetime, timestamp) {
    const seconds = datetime.unix() - timestamp;
    if(seconds < 0) {
      return null;
    }
    if(seconds < 60*60*24) {
      const value = this._transformSegments(seconds).map(({ value }) => {
        return this.convertToArray(value).join("");
      }).join(":");

      if(this.mode === 3 || this.mode === 4) {
        return this.customFormatDuration(seconds)
      } else{
        return `in ${value}`
      }
    }
    const value = datetime.format("Do MMMM YYYY, hh:mm A");

    if(this.mode === 3 || this.mode === 4) {
      return this.customFormatDuration(seconds, true)
    } else{
      return `on ${value}`
    }
  }

  customFormatDuration(seconds, isDays = null) {

    var durationStr 

    if(isDays){
      var days = Math.floor(seconds / (24 * 60 * 60));
      seconds %= (24 * 60 * 60);
    }

  
    var hours = Math.floor(seconds / (60 * 60));
    seconds %= (60 * 60);
  
    var minutes = Math.floor(seconds / 60);
    seconds %= 60;
    
    if(isDays){
      durationStr = days + "d " + hours + "h " + minutes + "m " + seconds + "s";
    }else{
      durationStr = hours + "h " + minutes + "m " + seconds + "s";
    }
    return durationStr;
  }

  private _displaySegments(segments) {
    const sumBy = _.sumBy(segments, segment => {
      return segment.duration * segment.value;
    })
    return sumBy > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const campaign = _.get(changes, 'campaign.currentValue', this.campaign);
    
    if(campaign === null) {
      return
    }

    this.campaign = changes.campaign.currentValue;

    this.takeUntilSubject.next(null)
    this.takeUntilSubject.complete()
    this.takeUntilSubject = new Subject();

    this.onInit()
  }
  
  onInit() {
    this.auctionControlMethod = _.get(this.campaign.campaignTypeData, 'auctionControlMethod', 0);
    this.startDateTime = moment(this.campaign.campaignTypeData.auctionStartTime * 1000);
    this.endDateTime = moment(this.campaign.campaignTypeData.auctionEndTime * 1000);

    this.autoCloseEnabled = _.get(this.campaign.campaignTypeData, 'autoCloseEnabled', 0);
    this.closeDateTime = moment(this.campaign.campaignTypeData.auctionCloseTime * 1000);

    this.startingIn$ = this.value$.pipe(
      map(timestamp => {
        const result = this.startDateTime.unix() - timestamp;
        return result > 0 ? result: 0;
      }),
      map(this._transformSegments.bind(this)),
      takeUntil(this.takeUntilSubject)
    );
    this.startingIn$.subscribe(value => {
      this.startingIn = value;
      this.cdr.markForCheck();
    });
    this.startingInFormat$ = this.value$.pipe(
      map((value) => {
        return this._transformFormat(this.startDateTime, value);
      }),
      takeUntil(this.takeUntilSubject)
    );
    this.startingInFormat$.subscribe(value => {
      this.startingInFormat = value;
      this.cdr.markForCheck();
    });
    this.isStartingIn$ = this.startingIn$.pipe(
      map(this._displaySegments.bind(this)),
      takeUntil(this.takeUntilSubject)
    );
    this.isStartingIn$.subscribe(value => {
      if (this.isStartingIn !== value) {
        this.toggleBtn.emit(value);
      }
      this.isStartingIn = value;
      this.cdr.markForCheck();
    });

    this.endsIn$ = this.value$.pipe(
      map(timestamp => {
        const result = this.endDateTime.unix() - timestamp;
        return result > 0 ? result: 0;
      }),
      map(this._transformSegments.bind(this)),
      takeUntil(this.takeUntilSubject)
    );
    this.endsIn$.subscribe(value => {
      this.endsIn = value;
      this.cdr.markForCheck();
    });
    this.endsInFormat$ = this.value$.pipe(
      map((value) => {
        return this._transformFormat(this.endDateTime, value);
      }),
      takeUntil(this.takeUntilSubject)
    );
    this.endsInFormat$.subscribe(value => {
      this.endsInFormat = value;
      this.cdr.markForCheck();
    });
    this.isEndsIn$ = this.isStartingIn$.pipe(
      filter(value => !value),
      concatMap(() => this.endsIn$),
      map(this._displaySegments.bind(this)),
      takeUntil(this.takeUntilSubject)
    );
    this.isEndsIn$.subscribe(value => {
      if (this.isEndsIn !== value) {
        this.toggleBtn.emit(value);
     }
      this.isEndsIn = value;
      this.cdr.markForCheck();
    });

    this.closingIn$ = this.value$.pipe(
      map(timestamp => {
        const result = this.closeDateTime.unix() - timestamp;
        return result > 0 ? result: 0;
      }),
      map(this._transformSegments.bind(this)),
      takeUntil(this.takeUntilSubject)
    );
    this.closingIn$.subscribe(value => {
      this.closingIn = value;
      this.cdr.markForCheck();
    });
    this.closingInFormat$ = this.value$.pipe(
      map((value) => {
        return this._transformFormat(this.closeDateTime, value);
      }),
      takeUntil(this.takeUntilSubject)
    );
    this.closingInFormat$.subscribe(value => {
      this.closingInFormat = value;
      this.cdr.markForCheck();
    });
    this.isClosingIn$ = this.closingIn$.pipe(
      map(this._displaySegments.bind(this)),
      takeUntil(this.takeUntilSubject)
    );
    this.isClosingIn$.subscribe(value => {
      if (this.isClosingIn !== value) {
        this.toggleBtn.emit(value);
      }
      this.isClosingIn = value;
      this.cdr.markForCheck();
    });

    const timestamp = moment().unix();
    const startingIn = this.startDateTime.unix() - timestamp;
    const endsIn = this.endDateTime.unix() - timestamp;
    const closingIn = this.closeDateTime.unix() - timestamp;
    
    this.startingIn = this._transformSegments(startingIn);
    this.startingInFormat = this._transformFormat(this.startDateTime, timestamp);
    this.isStartingIn = this._displaySegments(this.startingIn);

    this.endsIn = this._transformSegments(endsIn);
    this.endsInFormat = this._transformFormat(this.endDateTime, timestamp);
    this.isEndsIn = !this.isStartingIn && this._displaySegments(this.endsIn);

    this.closingIn = this._transformSegments(closingIn);
    this.closingInFormat = this._transformFormat(this.closeDateTime, timestamp);
    this.isClosingIn = this._displaySegments(this.closingIn);
  }

  ngOnDestroy() {
    this.takeUntilSubject.next(null);
    this.takeUntilSubject.complete();
  }

  getTemplateOutlet() {
    switch(this.mode) {
      case 1:
        return this.blockRef;
      case 2:
        return this.bloc2kRef;
    }
  }

  getTemplateOutletContex(segment, last) {
    return {
      label: segment.label, last,
      number: this.convertToArray(segment.value)
    };
  }

  convertToArray(number: number): string[] {
    const str = number.toString();
    const result = str.length < 2 ? `0${str}`: str;
    return result.split('');
  }

}